/**
 * The requirements screen.
 *
 * A curated screen in place of the generic EntityList for this one kind, for
 * the one thing a requirements list needs that the generic table cannot do:
 * IMPORTING, as the other half of the RTM round trip. Export has existed;
 * editing sixty statuses in a spreadsheet and bringing them back had no path at
 * all. Both buttons sit together because they are one workflow.
 *
 * It is also curated in its COLUMNS - `level`, `parent` and `verified_by`,
 * which the uniform listing drops to avoid a query per row. That is a data
 * difference, not a filtering one.
 *
 * **Filtering has been through two rounds, and the second amends the first.**
 * This screen used to carry a five-menu faceted bar filtering on the server,
 * while every other table had a plain box - and MUI's per-column filter menus
 * sat under both, unchosen. Issue #35 made it the same `EntityFilterBar` as
 * everywhere else, matching any column over rows held in the browser, with the
 * grid's column filters doing the faceting; a menu per column above a table
 * that filters by column looked like the same guard written twice.
 *
 * It was not quite. The community grid holds exactly ONE column condition, so
 * "level 3 AND subsystem structures" - the everyday way to slice a requirements
 * list - could not be asked at all (issue #68). So a row of QUICK FILTERS is
 * back, for the four columns people actually slice by: level, segment,
 * subsystem and status. They are client-side, over the same rows the search
 * and the grid already work on - NOT the server-side facets #35 removed, and
 * /api/facets stays gone. Three layers, in this order: the search box, then the
 * quick filters, then the grid's one column condition for anything else.
 *
 * The three derived shapes the old `state` menu offered are not lost - they are
 * column filters: 'unresolved' is status isAnyOf tbd/tbr/tbc, 'unverified' is
 * `verified_by` isEmpty, 'orphan' is `sources` isEmpty. 'orphan' is also a
 * Findings check, which is where a named check belongs.
 *
 * All three live in the URL (`search`, `quick`, `filter`), so a filtered list
 * is a link. The Export menu exports what they leave on screen. The sort is in
 * the URL too (`sort`), and all four are REMEMBERED: the breadcrumb on a
 * requirement's page and the sidebar both link to the bare path, and coming
 * back to the list put the filters back rather than starting over (issue #86,
 * see rememberedFilters).
 */

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import HelpTip from '../components/common/HelpTip'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CreateRequirementDialog from '../components/entity/CreateRequirementDialog'
import CreateRequirementTypeDialog from '../components/entity/CreateRequirementTypeDialog'
import EditRequirementTypeDialog from '../components/entity/EditRequirementTypeDialog'
import RequirementTypesTable from '../components/requirements/RequirementTypesTable'
import EmptyState from '../components/common/EmptyState'
import EntityFilterBar from '../components/entity/EntityFilterBar'
import ExportMenu from '../components/entity/ExportMenu'
import ImportCsvDialog from '../components/entity/ImportCsvDialog'
import QuickFilters from '../components/entity/QuickFilters'
import RequirementGrid from '../components/requirements/RequirementGrid'
import { applyQuick, hasQuick, readQuick, writeQuick } from '../components/entity/quickFilter'
import type { QuickSelection } from '../components/entity/quickFilter'
import { filterRows, readSearch, writeSearch } from '../components/entity/rowFilter'
import { detailPath } from '../components/entity/resultNav'
import type { ResultState } from '../components/entity/resultNav'
import { useColumnFilters } from '../components/entity/useColumnFilters'
import { useColumnSort } from '../components/entity/useColumnSort'
import { useRememberedFilters } from '../components/entity/rememberedFilters'
import { ApiError, PAGE_LIMIT, api } from '../api'
import { findKind, heading } from '../schema'
import { useCanWrite, useRefreshAppData, useSchema } from '../state/AppData'
import { useToasts } from '../state/Toasts'
import type { EntityRow, RequirementTypeRow } from '../schema'

export default function Requirements() {
  const schema = useSchema()
  const refreshSchema = useRefreshAppData()
  const toasts = useToasts()
  const navigate = useNavigate()
  const { allowed, reason } = useCanWrite()
  const [params, setParams] = useSearchParams()

  const [rows, setRows] = useState<EntityRow[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<ApiError | null>(null)
  const [creating, setCreating] = useState(false)
  const [declaringType, setDeclaringType] = useState(false)

  /**
   * Which table this page is showing.
   *
   * The types live BEHIND a tab rather than on a screen of their own: they are
   * part of writing requirements, not a separate subject, and a top-level entry
   * for something a program declares two of would be a navigation item nobody
   * finds.
   */
  const [view, setView] = useState<'requirements' | 'types'>('requirements')
  const [types, setTypes] = useState<RequirementTypeRow[]>([])
  const [typesError, setTypesError] = useState('')
  const [editingType, setEditingType] = useState<RequirementTypeRow | null>(null)

  // Loaded when the tab is opened rather than with the page: the requirements
  // screen is the one people land on, and a listing nobody has asked for should
  // not cost them a request.
  const loadTypes = () => {
    api
      .requirementTypes()
      .then((answer) => {
        setTypes(answer)
        setTypesError('')
      })
      .catch((problem) => setTypesError((problem as ApiError).message))
  }
  useEffect(() => {
    if (view === 'types') loadTypes()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view])

  const removeType = (row: RequirementTypeRow) => {
    api
      .deleteRequirementType(row.qualified_name)
      .then(() => {
        toasts.succeeded(`${row.name} removed`)
        loadTypes()
        // The type is gone from every picker too, and the schema is what
        // carries those.
        void refreshSchema()
      })
      .catch((problem) => setTypesError((problem as ApiError).message))
  }
  const [importing, setImporting] = useState(false)
  /** Bumped to re-read the listing after an import has written to it. */
  const [generation, setGeneration] = useState(0)
  /**
   * Which rows the GRID is showing, which is not what this screen can work out
   * for itself.
   *
   * The screen applies the search box and the quick filters; MUI applies the
   * column filters. So the count line said "6 of 10 requirements match" over
   * five rows - a number that was not the one on screen, in a change whose
   * whole point is not doing that. `null` until the grid has reported, so the
   * first paint uses the screen's own figure rather than flashing a zero.
   *
   * The ids, not just a count, because the Export menu sends them: "the 5 shown"
   * has to be the five the count line says, and both come from this one list.
   */
  const [visibleIds, setVisibleIds] = useState<string[] | null>(null)

  const kind = findKind(schema, 'requirement')

  // The filter IS the query string. Derived on every render rather than copied
  // into state, so there is only one of it and the back button works. The
  // column filters are in the URL too, but through useColumnFilters: the grid
  // also has to keep a condition somebody is half way through building.
  const search = readSearch(params)
  const { filterModel, applied, setFilterModel: changeColumns } = useColumnFilters()
  const { sortModel, setSortModel: changeSort } = useColumnSort()
  // Put the last filters back when the list is arrived at bare (issue #86).
  useRememberedFilters()

  /** Replace, not push: typing a filter should not fill up the back button. */
  const change = (next: string) => setParams(writeSearch(params, next), { replace: true })

  // The quick filters, in the URL under `quick` for the same reason. Parsed
  // once per URL, not per render: the object is a dependency below.
  const quick = useMemo(() => readQuick(params), [params])
  /**
   * Replace, not push, and from the CURRENT params rather than this render's:
   * the column filters write the URL too, and a write built from a stale copy
   * would undo whichever landed first.
   */
  const changeQuick = (next: QuickSelection) =>
    setParams((now) => writeQuick(now, next), { replace: true })

  /**
   * One request, not one per keystroke.
   *
   * The filter no longer reaches the server, so the debounce that used to sit
   * here has nothing left to protect. `generation` is still a dependency: an
   * import writes rows, and the listing has to be re-read to see them.
   */
  useEffect(() => {
    if (!kind) return
    let live = true
    setLoading(true)
    api
      .requirements({ limit: PAGE_LIMIT })
      .then((page) => {
        if (!live) return
        setRows(page.items)
        setTotal(page.total)
        setError(null)
      })
      .catch((failure: ApiError) => live && setError(failure))
      .finally(() => live && setLoading(false))

    return () => {
      live = false
    }
  }, [kind, generation])

  // The order is the contract: search, then quick, then the grid's own column
  // condition. `searched` is kept apart because the quick filters count their
  // choices over it.
  const searched = useMemo(() => filterRows(rows, search), [rows, search])
  const shown = useMemo(() => applyQuick(searched, quick), [searched, quick])

  /**
   * The columns offered as quick filters, labelled by the schema where it has a
   * label for them. `level` has none (it is read from the type chain, not
   * declared), so it falls back to the capitalised name like the grid's column.
   */
  const quickFields = useMemo(
    () =>
      ['level', 'segment', 'subsystem', 'status'].map((name) => ({
        name,
        label: kind?.fields.find((field) => field.name === name)?.label ?? heading(name),
      })),
    [kind],
  )

  /**
   * Whether the FETCH was capped - not whether the filter matched everything.
   *
   * Below the cap, a filter that finds nothing means the model has nothing,
   * because every row is in the browser. Above it, narrowing cannot reach what
   * was never fetched, and the notice has to say so.
   */
  const truncated = !loading && total > rows.length

  if (!kind) {
    return (
      <EmptyState
        title="This BEST does not have requirements"
        description="Nothing in /api/schema describes a requirement kind, so there is nothing to list."
      />
    )
  }

  // ANY filter counts as filtered: a column-filtered table that called itself
  // unfiltered would offer no way back to the whole list. The APPLIED
  // conditions, though - one still being built has not narrowed anything.
  const filtered = search.trim().length > 0 || hasQuick(quick) || applied.items.length > 0
  /**
   * The rows on screen: the grid's answer when it has one, else our own count.
   *
   * Nothing at all when the screen's own filters left nothing: the grid is not
   * mounted then, so what it last reported is a list from before the filter,
   * and would be counted (and offered for export) over an empty state.
   */
  const shownIds = shown.length === 0 ? [] : visibleIds
  const showing = shownIds?.length ?? shown.length

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h5">Requirements</Typography>
          {/* Was a sentence defining what a requirement is, printed under a
              heading reading Requirements. Behind the '?', the same rule every
              other listing follows - see EntityList. */}
          {kind.help && <HelpTip title={kind.help} label="What a requirement is" />}
        </Box>

        {/* Export and import together: the round trip through a spreadsheet is
            one workflow, and separating the halves is how people end up
            editing an export they have no way to bring back. */}
        {/* For requirements the plain file is the RTM and the details file is
            the generic export - see ExportMenu. The "shown" items are the ids
            the count line counts. */}
        <ExportMenu plural="requirements" standard="rtm" shownIds={shownIds} filtered={filtered} />
        <Tooltip title={allowed ? '' : reason} describeChild>
          <span>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setImporting(true)}
              disabled={!allowed}
            >
              Import CSV
            </Button>
          </span>
        </Tooltip>
        {kind.can_create && (
          <Tooltip title={allowed ? '' : reason} describeChild>
            <span>
              {/* OUTLINED and to the left of New: declaring a type is the rarer
                  act, done once so that writing requirements gets quicker. */}
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setDeclaringType(true)}
                disabled={!allowed}
              >
                New type
              </Button>
            </span>
          </Tooltip>
        )}
        {kind.can_create && (
          <Tooltip title={allowed ? '' : reason} describeChild>
            <span>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setCreating(true)}
                disabled={!allowed}
              >
                New
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      {/* The two tables this page has. 'Types' is where a requirement type is
          edited and removed - there is no other screen for one, because a type
          is not an entity with a row of its own. */}
      <Tabs
        value={view}
        onChange={(_event, next: 'requirements' | 'types') => setView(next)}
      >
        <Tab value="requirements" label="Requirements" />
        <Tab value="types" label="Types" />
      </Tabs>

      {view === 'types' && (
        <>
          {typesError && <Alert severity="error">{typesError}</Alert>}
          <RequirementTypesTable
            rows={types}
            allowed={allowed}
            reason={reason}
            onEdit={setEditingType}
            onDelete={removeType}
          />
        </>
      )}

      {view === 'requirements' && (
        <>
      <EntityFilterBar plural="requirements" value={search} onChange={change} />
      <QuickFilters
        rows={rows}
        counted={searched}
        fields={quickFields}
        value={quick}
        onChange={changeQuick}
      />

      <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline' }}>
        <Typography variant="body2" color="text.secondary">
          {loading
            ? 'Loading…'
            : filtered
              ? `${showing} of ${rows.length} ${rows.length === 1 ? 'requirement' : 'requirements'} match`
              : `${rows.length} ${rows.length === 1 ? 'requirement' : 'requirements'}`}
        </Typography>
        {/* Said rather than implied: a table holding 5,000 of 6,000 rows looks
            exactly like a table holding everything - and once the FETCH is
            capped, narrowing the filter cannot reach what was never fetched.
            So this says what the filter can see, not "narrow it further". */}
        {truncated && (
          <Typography variant="body2" color="warning.main">
            Showing the first {rows.length} of {total}. The filter searches only these.
          </Typography>
        )}
        {/* No "Clear filter" button here. EntityFilterBar carries a '✕' inside
            the box whenever there is something to clear, so a second control
            beside the count was two buttons with one job - and, because they
            had the same accessible name, indistinguishable to a screen reader
            or a test. One clear, in the bar every table shares. */}
      </Stack>

      {error ? (
        <Alert severity="error">{error.message}</Alert>
      ) : !loading && shown.length === 0 ? (
        <EmptyState
          title={filtered ? 'Nothing matches this filter' : 'No requirements yet'}
          description={
            filtered
              ? // Only claim completeness when the fetch WAS complete. Past the
                // cap this would be a promise the screen cannot keep.
                truncated
                ? `The filter searched the first ${rows.length} of ${total} requirements, so there may be matches it never saw.`
                : 'The filter searched every column of every requirement, so this really is none of them.'
              : 'A requirement states what the system shall do. State the first one, or import an RTM you already have.'
          }
          actions={
            filtered
              ? // Named for what it DOES rather than what it clears: 'Clear
                // filter' here would collide with the bar's own '✕', which is
                // still on screen behind this empty state.
                [
                  {
                    label: 'Show all requirements',
                    // Both of this screen's own filters. Clearing only the
                    // search left the quick filters holding the list empty
                    // under a button promising all of it.
                    onClick: () =>
                      setParams((now) => writeQuick(writeSearch(now, ''), {}), { replace: true }),
                  },
                ]
              : [
                  {
                    label: 'New requirement',
                    onClick: () => setCreating(true),
                    disabled: !allowed || !kind.can_create,
                  },
                ]
          }
        />
      ) : (
        <RequirementGrid
          kind={kind}
          rows={shown}
          loading={loading}
          onOpen={(row) => {
            // Carry the list (#109) so the detail view can step through it.
            const ids = shownIds ?? shown.map((entry) => String(entry.id))
            navigate(detailPath('requirements', row.id, params.toString()), {
              state: { resultIds: ids } satisfies ResultState,
            })
          }}
          filterModel={filterModel}
          onFilterModelChange={changeColumns}
          sortModel={sortModel}
          onSortModelChange={changeSort}
          onVisibleIdsChange={setVisibleIds}
        />
      )}

        </>
      )}

      {editingType && (
        <EditRequirementTypeDialog
          type={editingType}
          onClose={() => setEditingType(null)}
          onSaved={() => {
            setEditingType(null)
            loadTypes()
            // A changed template re-renders every statement it feeds, so the
            // requirements table behind this tab is stale too.
            void refreshSchema()
          }}
        />
      )}

      {declaringType && (
        <CreateRequirementTypeDialog
          kind={kind}
          onClose={() => setDeclaringType(false)}
          onCreated={() => {
            setDeclaringType(false)
            // The schema is what carries the new type into every picker, and it
            // is loaded once at start-up - so it has to be re-read or the type
            // just declared is invisible until a reload.
            void refreshSchema()
          }}
        />
      )}

      {creating && (
        <CreateRequirementDialog
          kind={kind}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false)
            navigate(`/requirements/${encodeURIComponent(id)}`)
          }}
        />
      )}

      {importing && (
        <ImportCsvDialog
          kind={kind}
          onClose={() => setImporting(false)}
          onImported={() => setGeneration((value) => value + 1)}
        />
      )}
    </Stack>
  )
}
